import { ChevronIcon, IconButton, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { HatTextTooltip } from "@/components/tooltip";
import {
  CostCell,
  CountCell,
  DailyTrendCell,
  GpuHoursCell,
  OccupancyCell,
  type GpuQuotaCellValues,
} from "./gpuQuotaCells";
import { GpuQuotaMemberRow, GpuQuotaOrphanRow, type GpuQuotaTableRow } from "./GpuQuotaTableRow";
import styles from "./GpuQuotaPage.module.css";

// 대상 다음에 오는 값 컬럼의 순서. 표의 컬럼과 하위 행의 칸이 이 목록 하나에서 자리를 정한다.
// 그래서 순서를 바꿔도 하위 행이 다른 칸에 그려지지 않는다.
export const GPU_QUOTA_VALUE_COLUMN_KEYS = [
  "activeGpus",
  "pendingGpus",
  "gpuHours",
  "dailyGpuHours",
  "cost",
  "sessionCount",
] as const;

export type GpuQuotaValueColumnKey = (typeof GPU_QUOTA_VALUE_COLUMN_KEYS)[number];

type ValueCellContext = {
  // 행 모델. 사람 행·꼬리 행·workspace 하위 행이 셀이 읽는 필드를 모두 가진다.
  values: GpuQuotaCellValues;
  // 한도 행이 있는지 나타낸다. 꼬리 행만 false 다.
  hasQuota: boolean;
  // 사용량 막대의 분모. 표에서 가장 많이 쓴 최상위 행의 GPU-hours 다.
  maxGpuHours: number;
};

// 값 컬럼 한 칸의 내용. 사람 행과 하위 행이 같은 함수로 그린다.
export function renderGpuQuotaValueCell(
  key: GpuQuotaValueColumnKey,
  { values, hasQuota, maxGpuHours }: ValueCellContext,
): ReactNode {
  switch (key) {
    case "activeGpus":
      return <OccupancyCell values={values} hasQuota={hasQuota} />;
    case "pendingGpus":
      return <CountCell count={values.pendingGpus} />;
    case "gpuHours":
      return <GpuHoursCell gpuHours={values.gpuHours} maxGpuHours={maxGpuHours} />;
    case "dailyGpuHours":
      return (
        <DailyTrendCell dailyGpuHours={values.dailyGpuHours} targetName={values.displayName} />
      );
    case "cost":
      return <CostCell cost={values.cost} />;
    case "sessionCount":
      return <CountCell count={values.sessionCount} />;
  }
}

const VALUE_COLUMN_SPECS: Record<
  GpuQuotaValueColumnKey,
  Pick<DataTableColumn<GpuQuotaTableRow>, "header" | "width" | "align" | "sortable">
> = {
  activeGpus: {
    header: (
      <HatTextTooltip
        text="지금 점유한 GPU 와 지금 적용 중인 한도입니다. 요일·시간대 스케줄이 걸려 있으면 한도가 기준 한도와 달라집니다. 사람 행은 선점한 workspace 들의 합이라, 하나라도 무제한이면 합계도 무제한입니다."
        placement="bottom"
      >
        <span className={styles.headerHint}>점유 / 한도</span>
      </HatTextTooltip>
    ),
    width: "minmax(230px, 270px)",
    sortable: "both",
  },
  pendingGpus: { header: "대기", width: "80px", align: "right", sortable: "both" },
  // 기간은 필터 바의 드롭다운이 말한다. 그래서 헤더에 기간을 다시 쓰지 않는다.
  gpuHours: { header: "GPU-hours", width: "200px", sortable: "both" },
  // 모양은 정렬할 값이 아니다.
  dailyGpuHours: { header: "일별 추이", width: "140px" },
  cost: {
    header: (
      <HatTextTooltip
        text="기간 GPU-hours 에 노드 계층별 단가를 곱한 값입니다. 실제 청구액과 다를 수 있습니다."
        placement="bottom"
      >
        <span className={styles.headerHint}>비용</span>
      </HatTextTooltip>
    ),
    width: "110px",
    align: "right",
    sortable: "both",
  },
  sessionCount: { header: "Session", width: "90px", align: "right", sortable: "both" },
};

type GpuQuotaColumnsOptions = {
  // 사람 행의 workspace 내역(행 아래 전폭 상세)을 펼치거나 접는다.
  onToggle: (row: GpuQuotaMemberRow) => void;
  maxGpuHours: number;
};

export function createGpuQuotaColumns({
  onToggle,
  maxGpuHours,
}: GpuQuotaColumnsOptions): readonly DataTableColumn<GpuQuotaTableRow>[] {
  return [
    {
      key: "displayName",
      header: "대상",
      width: "minmax(240px, 1fr)",
      sortable: "both",
      render: (row) => renderTarget(row, onToggle),
    },
    ...GPU_QUOTA_VALUE_COLUMN_KEYS.map(
      (key): DataTableColumn<GpuQuotaTableRow> => ({
        key,
        ...VALUE_COLUMN_SPECS[key],
        render: (row) =>
          renderGpuQuotaValueCell(key, {
            values: row,
            // 꼬리 행은 한도 행이 없다. "무제한" 이 아니라 값 없음이다.
            hasQuota: !(row instanceof GpuQuotaOrphanRow),
            maxGpuHours,
          }),
      }),
    ),
  ];
}

// 대상 셀. 사람 행(선점 요약)과 어느 사람에게도 속하지 않는 꼬리 행이 이 컬럼을 함께 쓴다.
// 펼친 workspace 내역은 이 셀이 아니라 행 아래 전폭 하위 행(GpuQuotaWorkspaceChild)에 표시된다.
function renderTarget(
  row: GpuQuotaTableRow,
  onToggle: (row: GpuQuotaMemberRow) => void,
): ReactNode {
  if (row instanceof GpuQuotaMemberRow) {
    return renderMemberTarget(row, onToggle);
  }
  return renderOrphanTarget(row as GpuQuotaOrphanRow);
}

function renderMemberTarget(
  row: GpuQuotaMemberRow,
  onToggle: (row: GpuQuotaMemberRow) => void,
): ReactNode {
  return (
    <span className={styles.memberHead}>
      <IconButton
        size="sm"
        variant="ghost"
        aria-expanded={row.isExpanded}
        aria-label={`${row.displayName} workspace 내역`}
        onClick={() => onToggle(row)}
      >
        <ChevronIcon
          className={row.isExpanded ? styles.caret : `${styles.caret} ${styles.caretCollapsed}`}
        />
      </IconButton>
      <span className={styles.workspaceCell}>
        <span className={styles.workspaceName}>{row.displayName}</span>
        <span className={styles.workspaceId}>{`workspace ${row.workspaces.length}곳`}</span>
      </span>
    </span>
  );
}

function renderOrphanTarget(row: GpuQuotaOrphanRow): ReactNode {
  return (
    <span className={styles.workspaceCell}>
      <span className={styles.workspaceName}>{row.displayName}</span>
      <span className={styles.workspaceId}>{row.workspaceId}</span>
      <span className={styles.noQuota}>upstream 에 없음</span>
    </span>
  );
}
